import FormSelect from "components/Common/Form/FormSelect";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import { formConvertErrorMessage, formValidate } from "helpers/form_helper";
import { useUpdateSaleTeams } from "hooks/sale-teams/useSaleTeams";
import { useHubList } from "hooks/hub-management/useHub";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useToasts } from "react-toast-notifications";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { saleTeamsTypeOptions, SALE_TEAM_TYPE } from "./utils";

const DetailSaleTeams = ({ dataDetail, isOpenModal, toggleModal, mode }) => {
  const { t } = useTranslation(["saleTeams"]);
  const { addToast } = useToasts();

  const [isDetail, setIsDetail] = useState(false);

  const getInitDefaultValues = () => {
    return {
      code: "",
      name: "",
      hub_id: null,
      type: null,
      note: "",
    };
  };

  const getInitControlFields = () => {
    return {
      display: {
        code: false,
        name: false,
        hub_id: false,
        type: false,
        note: false,
        btn_save: false,
      },
      disabled: {
        code: false,
        name: false,
        hub_id: false,
        type: false,
        note: false,
      },
    };
  };

  const [controlFields, setControlFields] = useState(getInitControlFields);

  const schema = yup.object().shape({
    code: yup
      .string()
      .required(t("form.validate.required", { title: t("form.code") })),
    name: yup
      .string()
      .required(t("form.validate.required", { title: t("form.name") })),
    hub_id: yup
      .object()
      .nullable()
      .when("type", {
        is: type => type?.value === SALE_TEAM_TYPE.TYPE_RETURN,
        then: schema =>
          schema.required(
            t("form.validate.required", { title: t("form.hub_id") })
          ),
        otherwise: schema => schema.nullable(),
      }),
    type: yup
      .object()
      .nullable()
      .required(t("form.validate.required", { title: t("form.type") })),
    note: yup.string().nullable(),
  });

  const mainForm = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: getInitDefaultValues(),
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, touchedFields, isSubmitting },
    setValue,
    getValues,
    setError,
    register,
    control,
    watch,
    clearErrors,
  } = mainForm;

  const typeValue = watch("type");

  useEffect(() => {
    if (typeValue?.value === SALE_TEAM_TYPE.TYPE_NO_RETURN) {
      setControlFields(prev => ({
        ...prev,
        disabled: {
          ...prev.disabled,
          hub_id: true,
        },
      }));

      const currentHubId = getValues("hub_id");
      if (currentHubId) {
        setValue("hub_id", null, { shouldValidate: false });
      }

      clearErrors("hub_id");
    } else if (typeValue?.value === SALE_TEAM_TYPE.TYPE_RETURN) {
      setControlFields(prev => ({
        ...prev,
        disabled: {
          ...prev.disabled,
          hub_id: false,
        },
      }));
    }
  }, [typeValue, setValue, getValues, clearErrors]);

  const onSuccessUpdateSaleTeams = _data => {
    toggleModal();
  };

  const onErrorUpdateSaleTeams = error => {
    if (error.code === 422 && error.fields.length > 0) {
      error.fields.forEach(item => {
        let fieldName = item.field;
        switch (item.field) {
          case "common":
            addToast(item.message, {
              appearance: "error",
              autoDismiss: true,
            });
            break;
          default:
            break;
        }
        setError(fieldName, {
          type: "manual",
          message: formConvertErrorMessage(item.message, t),
        });
      });
    }
    if (error.code !== 422) {
      toggleModal();
    }
  };

  const { isLoading: isUpdating, mutate: updateSaleTeams } = useUpdateSaleTeams(
    onSuccessUpdateSaleTeams,
    onErrorUpdateSaleTeams
  );

  const { data: centersData } = useHubList({
    params: {
      page: 1,
      sizePerPage: 10,
      columnFilter: {
        "filter[is_center]": "1",
        "filter[record_status]": "O",
      },
      sort: "-maker_date",
      keywords: "",
      expand: "maker,checker",
      fields: "name,id",
    },
    enabled: isOpenModal,
  });

  const hubOptions = useMemo(() => {
    if (!centersData?.items) return [];
    return centersData.items.map(center => ({
      label: center.name,
      value: center.id,
    }));
  }, [centersData]);

  const typeOptions = useMemo(() => {
    return saleTeamsTypeOptions.map(type => ({
      label: type.label,
      value: type.key,
    }));
  }, []);

  useEffect(() => {
    if (!isOpenModal) {
      setIsDetail(false);
      setControlFields(getInitControlFields());
      reset(getInitDefaultValues());
    }
  }, [isOpenModal]);

  useEffect(() => {
    if (isOpenModal && mode === "update" && dataDetail && !isDetail) {
      reset(getInitDefaultValues());

      setValue("code", dataDetail.code || "", { shouldValidate: true });
      setValue("name", dataDetail.name || "", { shouldValidate: true });
      setValue(
        "hub_id",
        dataDetail.hub
          ? { label: dataDetail.hub.name, value: dataDetail.hub.id }
          : null,
        { shouldValidate: true }
      );
      setValue(
        "type",
        dataDetail.type
          ? {
              label: saleTeamsTypeOptions.find(t => t.key === dataDetail.type)
                ?.label,
              value: dataDetail.type,
            }
          : null,
        { shouldValidate: true }
      );
      setValue("note", dataDetail.note || "", { shouldValidate: true });

      setControlFields({
        display: {
          ...getInitControlFields().display,
          code: true,
          name: true,
          hub_id: true,
          type: true,
          note: true,
          btn_save: true,
        },
        disabled: {
          ...getInitControlFields().disabled,
          code: true,
          name: true,
          type: true,
          note: true,
          hub_id: dataDetail.type === SALE_TEAM_TYPE.TYPE_NO_RETURN,
        },
      });

      setIsDetail(true);
    }
  }, [isOpenModal, mode, dataDetail, isDetail, reset, setValue]);

  const onSubmit = values => {
    const data = getValues();
    const submitData = {
      code: data.code,
      name: data.name,
      hub_id: data.hub_id?.value,
      type: data.type?.value,
      note: data.note || "",
    };

    updateSaleTeams({
      saleTeamsId: dataDetail.id,
      data: submitData,
    });
  };

  return (
    <Modal
      isOpen={isOpenModal}
      toggle={toggleModal}
      size="md"
      centered
      scrollable={false}
    >
      <ModalHeader toggle={toggleModal}>{t("title.update")}</ModalHeader>
      <ModalBody
        className="px-4"
        style={{ maxHeight: "70vh", overflowY: "auto" }}
      >
        {/* {isUpdating && <LoadingFormNormal />} */}
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row className="g-2">
            {controlFields.display.name && (
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label htmlFor="name">
                    {t("form.name")} <span className="text-danger">*</span>
                  </Label>
                  <input
                    type="text"
                    disabled={controlFields.disabled.name}
                    className={`form-control ${formValidate(
                      errors,
                      touchedFields,
                      "name"
                    )}`}
                    placeholder={t("placeholder.enter", {
                      title: t("form.name"),
                    })}
                    {...register("name")}
                  />
                  {errors.name && (
                    <MessageFormValidate error={errors.name.message} />
                  )}
                </FormGroup>
              </Col>
            )}
            {/* {controlFields.display.code && (
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label htmlFor="code">
                    {t("form.code")} <span className="text-danger">*</span>
                  </Label>
                  <input
                    type="text"
                    disabled={controlFields.disabled.code}
                    className={`form-control ${formValidate(
                      errors,
                      touchedFields,
                      "code"
                    )}`}
                    placeholder={t("placeholder.enter", {
                      title: t("form.code"),
                    })}
                    {...register("code")}
                  />
                  {errors.code && (
                    <MessageFormValidate error={errors.code.message} />
                  )}
                </FormGroup>
              </Col>
            )} */}
            {controlFields.display.hub_id && (
              <Col md={6}>
                <FormGroup className="mb-3">
                  <FormSelect
                    mainForm={mainForm}
                    fieldName="hub_id"
                    t={t}
                    dataList={hubOptions}
                    isRequired={typeValue?.value === SALE_TEAM_TYPE.TYPE_RETURN}
                    title={t("form.hub_id")}
                    isError={false}
                    isDisabled={controlFields.disabled.hub_id}
                  />
                  {errors.hub_id && (
                    <MessageFormValidate error={errors.hub_id.message} />
                  )}
                </FormGroup>
              </Col>
            )}
            {controlFields.display.type && (
              <Col md={12}>
                <FormGroup className="mb-3">
                  <FormSelect
                    mainForm={mainForm}
                    fieldName="type"
                    t={t}
                    dataList={typeOptions}
                    isRequired
                    title={t("form.type")}
                    isError={false}
                  />
                  {errors.type && (
                    <MessageFormValidate error={errors.type.message} />
                  )}
                </FormGroup>
              </Col>
            )}
            {controlFields.display.note && (
              <Col md={12}>
                <FormGroup className="mb-3">
                  <Label htmlFor="note">{t("form.note")}</Label>
                  <textarea
                    disabled={controlFields.disabled.note}
                    className={`form-control ${formValidate(
                      errors,
                      touchedFields,
                      "note"
                    )}`}
                    // placeholder={t("placeholder.enter", {
                    //   title: t("form.note"),
                    // })}
                    rows={1}
                    {...register("note")}
                  />
                  {errors.note && (
                    <MessageFormValidate error={errors.note.message} />
                  )}
                </FormGroup>
              </Col>
            )}
          </Row>
        </Form>
      </ModalBody>
      <ModalFooter className="px-4">
        {controlFields.display.btn_save && (
          <Button
            color="primary"
            className="btn-label"
            type="button"
            onClick={() => {
              handleSubmit(onSubmit)();
            }}
            disabled={isUpdating}
          >
            <i className="far fa-save label-icon"></i>
            {t("form.update")}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default DetailSaleTeams;
